import type {FeedSource} from "../feedSource";
import type {EopFeedTile, FeedTileConfig} from "./feedTile";
import {FeedEntry} from "../feedEntry";
import {EopImageSpinner, type EopOverlaySpinner} from "../../../page/elements/spinner";
import {Resolution} from "../../../common/resolution";
import {resolve} from "../../../container";
import {MultiRowFeedPreviewSize, SingleRowFeedPreviewSize} from "./feedPreviewSize";
import {schedule} from "../../../common/utils/promises";
import {html, LitElement, type PropertyValues, type TemplateResult} from "lit";
import Styles from "./feedPreviewSlider.lit.scss";
import GridStyles from "./feedPreviewGrid.lit.scss";
import {property, query, state} from "lit/decorators.js";
import {ANIMATION_DURATION} from "../../../common/gridLayout";
import {Timeout} from "../../../common/timeout";
import {ELEMENT_SCROLL_PADDING, ScrollService} from "../../../common/scroll";
import type {Maybe, Nullable} from "../../../bootstrap/common/types";
import {Dictionary} from "../../../page/elements/dictionary";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";

export class FeedEntriesCollector {
    public constructor(private feedSource: FeedSource) {
    }

    public async collectFeedEntries(size: number, offset: number, lastVisitDate?: number): Promise<FeedEntry[]> {
        const feeds = await this.feedSource.fetch(size, offset, lastVisitDate);
        return feeds.map(feed => {
            return FeedEntry.fromResponse(feed)
                .inChannelContext(this.feedSource.channels());
        });
    }
}

export abstract class AbstractFeedPreviewGrid<F extends FeedSource> extends LitElement {

    public static readonly styles = GridStyles;

    @property({attribute: "character-limit", type: Number})
    public characterLimit: number = 180;
    @property({attribute: "information-density"})
    public informationDensity: string = "";
    @property({attribute: "card-boxing"})
    public cardBoxing: string = "";
    @property({attribute: "linked-topics", type: Boolean})
    private linkedTopics: boolean = false;
    @property({attribute: "use-page-overlay", type: Boolean})
    public usePageOverlay: boolean = false;
    @property({attribute: "context-preserving-page-overlay", type: Boolean})
    public contextPreservingPageOverlay: boolean = false;
    @property({attribute: "size"})
    public size: Maybe<string> = undefined;
    @property({attribute: "arrangement"})
    private arrangement: string = "";
    @state()
    private feedEntries: FeedEntry[] = [];
    @state()
    private limit: number;

    @query("eop-image-spinner")
    protected fetchSpinner: EopImageSpinner;
    @query("eop-overlay-spinner")
    protected fetchMoreSpinner: EopOverlaySpinner;
    @query(".feed-tiles")
    protected feedTilesContainer: HTMLElement;

    private dictionary: Dictionary;
    private feedEntriesCollector: FeedEntriesCollector;
    private feedTilesConfig: FeedTileConfig;
    protected enableMore: boolean;

    protected constructor(
        protected feedSource: F,
        private resolution: Resolution = resolve(Resolution),
        private scrollService: ScrollService = resolve(ScrollService),
        private timeout: Timeout = resolve(Timeout)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.dictionary = Dictionary.of(this);
        this.configure();
        this.limit = new MultiRowFeedPreviewSize(this.size, this.resolution, this.arrangement).getTileAmount();
        this.feedEntriesCollector = new FeedEntriesCollector(this.feedSource);
        this.feedTilesConfig = {
            arrangement: this.arrangement,
            informationDensityClass: this.informationDensity,
            cardBoxingClass: this.cardBoxing,
            usePageOverlay: this.usePageOverlay,
            contextPreservingPageOverlay: this.contextPreservingPageOverlay,
            topicsLinked: this.linkedTopics
        };

        if (this.arrangement) {
            this.classList.add(this.arrangement);
        }
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        void schedule(this.fetchSpinner.spinWhile(this.fetchInitial()))
            .as("fetch");
    }

    protected abstract configure(): void;

    public render(): TemplateResult {
        return html`
            <eop-overlay-spinner></eop-overlay-spinner>
            <eop-image-spinner></eop-image-spinner>
            ${this.renderTiles()}
            ${this.renderFetchMoreButton()}
        `;
    }

    private renderTiles(): Nullable<TemplateResult> {
        if (this.feedEntries.isEmpty()) {
            return null;
        }
        return html`
            <div class="feed-tiles">${(this.renderFeedTiles())}
            </div>`;
    }

    private renderFeedTiles(): TemplateResult[] {
        return this.feedEntries.map(entry => html`
            <eop-feed-tile .entry=${entry} .config=${this.feedTilesConfig}></eop-feed-tile>`);
    }

    private renderFetchMoreButton(): Nullable<TemplateResult> {
        const fetchMoreLabel = this.dictionary.translate("FEED_FETCH_MORE");
        if (this.showFetchMore()) {
            return html`
                <div class="fetch-more-button">
                    <button type="button" class="primary" @click=${this.fetchMoreHandler} ${trackingButtonInteract(fetchMoreLabel)}>
                        ${fetchMoreLabel}
                    </button>
                </div>
            `;
        }
        return null;
    }

    protected fetchMoreHandler(): void {
        const offset = this.feedEntries.length;
        void schedule(this.fetchMoreSpinner.spinWhile(this.fetchMore(offset)))
            .as("more");
    }

    private async fetchInitial(): Promise<void> {
        this.feedEntries = await this.fetchFeedEntries(this.limit, 0);
    }

    private async fetchMore(offset: number): Promise<void> {
        const newEntries = await this.fetchFeedEntries(this.limit, offset);
        this.feedEntries = this.feedEntries.slice(0, offset).concat(newEntries);
        this.scrollToNextElement(offset);
    }

    private showFetchMore(): boolean {
        return this.enableMore
            && this.feedSource.total() > this.feedEntries.length;
    }

    private scrollToNextElement(offset: number): void {
        void this.timeout.delay(() => {
            const nextTile = this.renderRoot.querySelectorAll<EopFeedTile>("eop-feed-tile").item(offset);
            void this.scrollService.scrollToElement(this.feedTilesContainer, -nextTile.offsetTop + ELEMENT_SCROLL_PADDING, ANIMATION_DURATION);
        });
    }

    private async fetchFeedEntries(size: number, offset: number): Promise<FeedEntry[]> {
        return await this.feedEntriesCollector.collectFeedEntries(size, offset);
    }
}

export abstract class AbstractFeedPreviewSlider<F extends FeedSource> extends LitElement {

    public static readonly styles = Styles;

    @property({attribute: "character-limit", type: Number})
    public characterLimit: number = 180;
    @property({attribute: "information-density"})
    public informationDensity: string = "";
    @property({attribute: "card-boxing"})
    public cardBoxing: string = "";
    @property({attribute: "linked-topics", type: Boolean})
    private linkedTopics: boolean = false;
    @property({attribute: "use-page-overlay", type: Boolean})
    public usePageOverlay: boolean = false;
    @property({attribute: "context-preserving-page-overlay", type: Boolean})
    public contextPreservingPageOverlay: boolean = false;
    @property({attribute: "size"})
    public size: Maybe<string> = undefined;

    @state()
    protected feedEntries: FeedEntry[] = [];
    @state()
    protected limit: number;

    @query("eop-image-spinner")
    protected spinner: EopImageSpinner;

    private feedTileCollector: FeedEntriesCollector;
    private feedTilesConfig: FeedTileConfig;

    protected constructor(
        protected feedSource: F,
        protected resolution: Resolution = resolve(Resolution)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.configure();
        this.setTileAmount();

        this.feedTileCollector = new FeedEntriesCollector(this.feedSource);
        this.feedTilesConfig = {
            arrangement: "",
            informationDensityClass: this.informationDensity,
            cardBoxingClass: this.cardBoxing,
            usePageOverlay: this.usePageOverlay,
            contextPreservingPageOverlay: this.contextPreservingPageOverlay,
            topicsLinked: this.linkedTopics
        };
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        void schedule(this.spinner.spinWhile(this.fetchFeedEntries(this.limit, 0)
            .then(entries => this.feedEntries = entries)))
            .as("fetch");
    }

    protected abstract configure(): void;

    protected setTileAmount(): void {
        this.limit = new SingleRowFeedPreviewSize(this.size, this.resolution).getTileAmount();
    }

    public render(): TemplateResult {
        return html`
            <eop-image-spinner></eop-image-spinner>
            <div class="multi-carousel">
                ${this.renderMultiCarousel()}
            </div>
        `;
    }

    protected renderMultiCarousel(): Nullable<TemplateResult> {
        if (this.feedEntries.isEmpty()) {
            return null;
        }
        return html`
            <eop-multi-carousel>
                ${this.renderTiles()}
            </eop-multi-carousel>
        `;
    }

    protected renderTiles(): Nullable<TemplateResult> {
        if (this.feedEntries.isEmpty()) {
            return null;
        }
        const feedEntries = this.feedEntries.map(entry => html`
            <eop-feed-tile slot="slider-content" .entry=${entry} .config=${this.feedTilesConfig}></eop-feed-tile>`);
        return html`${feedEntries}`;
    }

    private async fetchFeedEntries(size: number, offset: number): Promise<FeedEntry[]> {
        return await this.feedTileCollector.collectFeedEntries(size, offset);
    }
}